<template>
  <drawer @close="cancel()" width="680">
    <ValidationObserver ref="validator">
      <transition-group name="slide">
        <v-container key="step-1" class="px-5 py-12 pa-md-20" v-if="step === 1">
          <v-card-title
            class="display-1 font-weight-bold pa-0 mb-8 letter-spacing-normal"
          >
            Report Sightings
          </v-card-title>
          <v-row
            class="flex-column flex-md-row justify-lg-space-between align-center"
          >
            <v-col sm="4">
              <div class="text-left font-weight-bold">Select Date</div>
            </v-col>
            <v-col sm="8">
              <div class="text-right">
                <DatePicker
                  name="reported_at"
                  rules="required"
                  :min="group.check_in"
                  v-model="request.reported_at"
                  label="Select Date"
                />
              </div>
            </v-col>
          </v-row>
          <v-row
            class="flex-column flex-md-row justify-lg-space-between align-center"
          >
            <v-col sm="4">
              <div class="text-left font-weight-bold">Select Tour</div>
            </v-col>
            <v-col sm="8">
              <div id="toursSelect" class="position-relative">
                <ValidationProvider
                  name="tour"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-autocomplete
                    :disabled="!!currentSighting.id"
                    :search-input.sync="toursFilter"
                    v-model="request.tour_id"
                    :items="tours"
                    :multiple="false"
                    attach="#toursSelect"
                    item-text="name"
                    item-value="id"
                    :error-messages="errors"
                    label="Search"
                    append-icon="$search"
                    class="font-weight-bold"
                    color="black"
                  />
                </ValidationProvider>
              </div>
            </v-col>
          </v-row>
        </v-container>
        <MemberSightings
          v-else-if="step > 1 && step <= group.members.length + 1"
          :member="group.members[step - 2]"
          :index="step - 2"
          key="step-2"
          :request="request"
          :isEditMode="!!(jumpStep > 1 && sightingsData.length)"
        />
        <Confirm
          @edit="edit"
          v-else
          key="step-3"
          :group="group"
          :sightings="request"
          :fetchingSummary="fetchingSummary"
        />
      </transition-group>
    </ValidationObserver>
    <template v-slot:append>
      <div v-if="jumpStep === group.members.length + 2" class="px-24 py-12">
        <v-btn block large class="" @click="cancel()">
          Back
        </v-btn>
      </div>
      <div
        v-else
        class="px-5 px-md-24 py-md-12 d-flex flex-column flex-md-row justify-lg-space-between"
      >
        <v-btn
          large
          class="white px-14 mb-4 mb-md-0"
          min-width="45%"
          @click="step === 1 ? cancel() : back()"
        >
          {{ step === 1 ? 'Cancel' : 'Back' }}
        </v-btn>
        <v-btn
          :disabled="
            nextStepText === 'Submit' &&
              (!request.attendees || !request.attendees.length)
          "
          min-width="45%"
          large
          class="primary px-14"
          @click="next()"
        >
          {{ nextStepText }}
        </v-btn>
      </div>
    </template>
  </drawer>
</template>

<script>
import TourModel from '@models/Tour';
import Request from '@request/GroupSightingsRequest';
import GroupModel from '@models/Group';

export default {
  name: 'SightingsReport',
  components: {
    MemberSightings: () => import('./_partials/MemberSightings'),
    Drawer: () => import('@layouts/_partials/Drawer'),
    Confirm: () => import('./_partials/Confirm'),
    DatePicker: () => import('@components/utilities/DatePicker'),
  },
  props: {
    group: {
      type: Object,
      required: true,
      validator(data) {
        if (!data.id) {
          throw new Error('Invalid group object provided!');
        }
        return true;
      },
    },
    jumpStep: {
      type: Number,
      default: 1,
    },
    sightingsData: {
      type: Array,
      default: () => [],
    },
    currentSighting: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      toursFilter: '',
      tourModel: new TourModel({
        itemsPerPage: 50,
        page: 1,
        tourism_operator_id: this.group.tourism_operator_id ?? false,
      }),
      sightings: [],
      groupModel: new GroupModel(),
      tours: [
        ...(this.currentSighting && this.currentSighting.tour
          ? [this.currentSighting.tour]
          : []),
      ],
      step: this.jumpStep,
      component: '',
      request: new Request(
        this.$refs,
        this.formattedData(this.currentSighting || {})
      ),
      fetchingSummary: false,
    };
  },
  computed: {
    nextStepText() {
      if (this.step < 1) {
        return '';
      }
      if (this.step === 1) {
        return 'Proceed';
      } else if (this.step <= this.group.members.length) {
        return 'Next Guest';
      } else if (this.step === this.group.members.length + 1) {
        return 'Proceed';
      }
      return 'Submit';
    },
  },
  watch: {
    'group.id': {
      handler() {
        this.fetchSightings();
      },
    },
    jumpStep(step) {
      this.step = step || 1;
    },
    toursFilter(name) {
      if (this.request.tour_id) {
        let tour = this.tours.find((tour) => tour.id === this.request.tour_id);
        if (tour && tour.name === name) {
          return;
        }
      }
      if (this.tourModel.fetched) {
        this.fetchTours();
      }
    },
  },
  methods: {
    edit(editStep) {
      this.step = editStep;
    },
    async fetchSightings() {
      if (!this.group.sightings_reported) {
        return;
      }
      this.fetchingSummary = true;
      let response = await this.groupModel.fetchSightings(this.group.id);
      if (response) {
        this.sightings = response.data;
        this.fetchingSummary = false;
      }
    },
    async fetchTours() {
      this.tourModel.setFilters({
        name: this.toursFilter,
        excludes: this.sightings
          .reduce((ids, sighting) => ids + ' ' + sighting.tour_id, '')
          .trim()
          .replace(/\s/g, ', '),
      });
      let response = await this.tourModel.get();
      if (response) {
        this.tours = response.data;
      }
    },
    formattedData(data) {
      return {
        id: data.id || '',
        reported_at: data.reported_at || this.$moment().format('YYYY-MM-DD'),
        tour_id: data.tour_id || '',
        group_id: this.group.id,
        attendees: data.attendees || [],
      };
    },
    cancel(data) {
      this.$parent._data.jumpStep = 1;
      this.$emit('exit', data);
    },
    back() {
      this.step--;
    },
    async next() {
      if (this.step === 1) {
        if (await this.$refs.validator.validate()) {
          this.step++;
        }
        return;
      }
      if (this.step <= this.group.members.length + 1) {
        this.step++;
      } else {
        if (!this.request.submitting && (await this.request.validate())) {
          let response = await this.request.submit();
          if (response) {
            this.cancel(response.data);
          }
        }
      }
    },
  },
  async mounted() {
    if (this.sightingsData.length && this.jumpStep > 1) {
      this.sightings = this.sightingsData;
      this.tours = [this.currentSighting.tour];
      this.request.reinit(this.currentSighting);
    } else {
      await this.fetchSightings();
      this.fetchTours();
    }
  },
};
</script>
